﻿import { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  ImageListItem,
  ImageListItemBar,
  Stack,
  Grid,
  ImageList,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import { SAVE_CLIENT_EQUIPMENT_INSPECTION_ITEMS } from "../../constant/Apipath";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import { ClientEnglishFallback } from "../constant/ClientEnglishFallback";
import { BILightGrey } from "../../assets/helpers";
import PictureViewer from "../../common/PictureViewer";

const getColor = (answer) => {
  if (answer === "YES") return "#C3E6BA";
  if (answer === "NO") return "#FCBDBD";
  if (answer === "N/A") return "#EBEBEB";
};

const getTextColor = (answer) => {
  if (answer === "YES") return "#228C07";
  if (answer === "NO") return "#6E0C0C";
  if (answer === "N/A") return "#4F4F4F";
};

const InspectionSection = ({
  inspectionData,
  setSelectedAnswers,
  selectedAnswers,
  setIsImgChanged,
  isImgChanged,
  formItems,
  setFormItems
}) => {
  const theme = useTheme();
  const { loginData, selectedClientData, multiLanguageData } = useSelector((state) => state?.main);
  const LANGUAGE = ClientEnglishFallback.InspectionSection(
    multiLanguageData?.InspectionSection || {}
  );
  const {
    TEXT_ADD_PICTURES,
    TEXT_INSPECTION,
    TEXT_LOADING_DATA,
    TEXT_NO_FORM_ITEMS,
    TEXT_YES,
    TEXT_NO,
    TEXT_NA
  } = LANGUAGE;

  const answersType = [
    {
      label: TEXT_YES,
      value: "YES"
    },
    {
      label: TEXT_NO,
      value: "NO"
    },
    {
      label: TEXT_NA,
      value: "N/A"
    }
  ];

  const [questionExpanded, setQuestionExpanded] = useState(0);
  const [selectedQuestionForImage, setSelectedQuestionForImage] = useState(null);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openImageViewer, setOpenImageViewer] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const userId = loginData?.userInformation?.UserId;

  const fetchClientEquipmentInspectionForms = useCallback(async () => {
    try {
      let obj = {
        PageNr: 1,
        NrOfRecPerPage: 20,
        FullSearch: "",
        UserId: userId,
        IncludeRecordNr: true,
        FetchAllowedRecordsOnly: true,
        SearchList: [
          {
            InspectionTypeId: inspectionData,
            ClientId: selectedClientData?.ClientId
          }
        ]
      };
      const response = await Postrequestcall(
        SAVE_CLIENT_EQUIPMENT_INSPECTION_ITEMS,
        obj,
        loginData?.accessToken
      );

      if (response?.data?.data) {
        setFormItems(response?.data?.data);
      } else {
        setError("No data found.");
      }
    } catch (error) {
      setError("Failed to fetch inspection data.");
      console.error("fetchClientEquipmentInspectionForms->Error: ", error);
    } finally {
      setIsLoading(false);
    }
  }, [inspectionData, selectedClientData, loginData?.accessToken, userId, setFormItems]);

  useEffect(() => {
    fetchClientEquipmentInspectionForms();
  }, [fetchClientEquipmentInspectionForms]);

  const handleAnswer = (questionId, answer) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: answer }));
    setQuestionExpanded((prev) => prev + 1);
  };

  const handleImageChange = (e, questionId) => {
    const files = Array.from(e.target.files);
    const imageObjs = files.map((file) => ({
      path: URL.createObjectURL(file),
      name: file.name
    }));
    setIsImgChanged((prev) => ({
      ...prev,
      [questionId]: prev[questionId] ? [...prev[questionId], ...imageObjs] : [...imageObjs]
    }));

    e.target.value = "";
  };

  const handleRemoveImage = (questionId, index) => {
    const filtered = isImgChanged[questionId]?.filter((_, i) => i !== index);
    setIsImgChanged((prev) => ({ ...prev, [questionId]: filtered }));
  };

  const handleAddPicturesClick = (questionId) => {
    setSelectedQuestionForImage(questionId);
    fileInputRef.current.click();
  };

  const handleImageClick = (imagePath) => {
    setSelectedImage(imagePath);
    setOpenImageViewer(true);
  };

  const handleCloseImageViewer = () => {
    setOpenImageViewer(false);
    setSelectedImage(null);
  };

  return (
    <Box sx={{ backgroundColor: "white", p: 2 }}>
      <Box sx={{ p: 2,  justifyContent: "space-between",  display: "flex" }}  >
        <Typography variant="h6">{TEXT_INSPECTION}</Typography>
        <Typography>
          {Object.keys(selectedAnswers)?.length}/{formItems?.length}
        </Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ textAlign: "center", p: 3 }}>
          <CircularProgress sx={{ color: selectedClientData?.AccentColor }} />
          <Typography>{TEXT_LOADING_DATA}</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: "center", p: 3 }}>
          <Typography sx={{ color: "error" }}>{error}</Typography>
        </Box>
      ) : formItems?.length === 0 ? (
        <Box sx={{ textAlign: "center", p: 3 }}>
          <Typography>{TEXT_NO_FORM_ITEMS}</Typography>
        </Box>
      ) : (
        formItems?.map((item, index) => (
          <Card
            key={item.id}
            variant="outlined"
            sx={{
              mb: 2,
              mx: 2,
              backgroundColor: selectedAnswers[item.id] ? "#f0f0f0" : "white",
              borderColor: selectedAnswers[item.id] ? "#ccc" : "#ddd"
            }}>
            <CardContent>
              <Box
                sx={{ alignItems: "center",  justifyContent: "space-between",  display: "flex" }}
                
                
                onClick={() => setQuestionExpanded(questionExpanded === index ? null : index)}
                sx={{ cursor: "pointer" }}>
                <Typography
                  sx={{
                    color: selectedAnswers[item.id] ? "gray" : "black"
                  }}>
                  {item.description}
                </Typography>

                <Box sx={{ gap: 1,  alignItems: "center",  display: "flex"  }}  >
                  {isImgChanged[item.id]?.length > 0 && <ImageIcon color="disabled" />}
                  {selectedAnswers[item.id] && (
                    <Chip
                      label={
                        selectedAnswers[item.id] === "YES"
                          ? TEXT_YES
                          : selectedAnswers[item.id] === "NO"
                            ? TEXT_NO
                            : TEXT_NA
                      }
                      size="small"
                      sx={{
                        backgroundColor: getColor(selectedAnswers[item.id]),
                        color: getTextColor(selectedAnswers[item.id])
                      }}
                    />
                  )}
                </Box>
              </Box>

              {questionExpanded === index && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    variant="contained"
                    color="primary"
                    onClick={() => handleAddPicturesClick(item.id)}
                    sx={{
                      mb: 2,
                      color: selectedClientData?.AccentColor,
                      cursor: "pointer"
                    }}>
                    {TEXT_ADD_PICTURES}
                  </Box>

                  {isImgChanged[item.id]?.length > 0 && (
                    <Box sx={{ minHeight: isImgChanged[item.id]?.length === 0 ? 90 : "auto" }}>
                      {isImgChanged[item.id]?.length !== 0 && (
                        <Stack>
                          <Grid
                            size={{ xs: 12, sm: 12, md: 12 }}
                            sx={{ height: isSmallScreen ? 90 : 130 }}>
                            <ImageList
                              gap={7.5}
                              sx={{
                                width: "100%",
                                height: "100%",
                                margin: 0,
                                padding: 0,
                                flexWrap: "nowrap",
                                overflowX: "auto",
                                display: "flex",
                                pb: 0.5,
                                cursor: "pointer",
                                borderRadius: "10px",
                                backgroundColor: "white"
                              }}>
                              {isImgChanged[item.id]?.map((picItem, imgIdx) => (
                                <ImageListItem
                                  key={imgIdx}
                                  sx={{ minWidth: isSmallScreen ? 90 : 130 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleImageClick(picItem.path);
                                  }}>
                                  <img
                                    src={picItem?.path}
                                    style={{
                                      borderRadius: 10,
                                      width: isSmallScreen ? 90 : 130,
                                      height: isSmallScreen ? 90 : 130,
                                      objectFit: "cover",
                                      cursor: "pointer"
                                    }}
                                    alt="Expenses"
                                    loading="lazy"
                                  />
                                  <ImageListItemBar
                                    sx={{ background: "none", padding: 1 }}
                                    position="top"
                                    actionIcon={
                                      <IconButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveImage(item.id, imgIdx);
                                        }}
                                        size="small"
                                        sx={{
                                          backgroundColor: "rgba(0,0,0,0.3)",
                                          color:
                                            selectedClientData?.AccentColor !== undefined &&
                                              selectedClientData?.AccentColor
                                              ? selectedClientData?.AccentColor
                                              : BILightGrey
                                        }}>
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    }
                                    actionPosition="right"
                                  />
                                </ImageListItem>
                              ))}
                            </ImageList>
                          </Grid>
                        </Stack>
                      )}
                    </Box>
                  )}

                  <Box sx={{ gap: 2,  mt: 2,  display: "flex"  }}  >
                    {answersType?.map((ans) => (
                      <Button
                        key={ans?.value}
                        variant={selectedAnswers[item.id] === ans?.value ? "contained" : "outlined"}
                        sx={{
                          backgroundColor:
                            selectedAnswers[item.id] === ans?.value
                              ? getColor(ans?.value)
                              : "white",
                          color:
                            selectedAnswers[item.id] === ans?.value
                              ? getTextColor(ans?.value)
                              : "black",
                          borderColor: getColor(ans?.value)
                        }}
                        onClick={() => handleAnswer(item.id, ans?.value)}>
                        {ans?.label}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}

      <PictureViewer
        isOpen={openImageViewer}
        onClose={handleCloseImageViewer}
        pictures={selectedImage ? [{ src: selectedImage }] : []}
        viewerIndex={0}
      />

      <input
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleImageChange(e, selectedQuestionForImage)}
        ref={fileInputRef}
        style={{ display: "none" }}
      />
    </Box>
  );
};

export default InspectionSection;

